import { Box, Button, Typography } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import Sync from '@material-ui/icons/Sync';
import { Grid, GridCellProps, GridColumn, GridToolbar } from '@progress/kendo-react-grid';
import _ from 'lodash';
import React from 'react';
import { useState } from 'react';
import { useQueryClient } from 'react-query';
import { useToastify } from '../../../../../../Hooks';
import { InlineEditingActionsCell } from '../../../../../../Hooks/useKendoGrid';
import useStylesGrid from '../../../../../../styles/grid.styles';
import { CheckBoxCell } from '../../../../../../ui/CheckBoxCell';
import LoadingIndicator from '../../../../../../ui/old/Feedback/LoadingIndicator';
import useAsiteCustomAttributeTypesQuery from '../../../../hooks/useCodeTables/useAsiteCustomAttributeTypesQuery';
import useSecurity from '../../../../hooks/useSecurity/useSecurity';
import useStyles from '../../../../styles/useStyles';
import { EAtributeTypes } from '../../../../types';
import ActionConfirmationDialogComponent from '../../../../ui-kit/ActionConfirmationDialogComponent';
import { AttributeCell } from '../../../../ui-kit/AttributeCell';
import { DropDownCell } from '../../../../ui-kit/DropDownCell';
import { PoiDropdownCell } from '../../../../ui-kit/PoiDropdownCell';
import { StatusCell } from '../../../../ui-kit/StatusCell';
import PathFolderModal from '../../../ConfigurationDocumentPlaceholder/components/PathFolderModal';
import { usePlaceholderQuery } from '../../../ConfigurationDocumentPlaceholder/hooks/usePlaceholderManagement';
import useAddForceSyncMutation from '../../hooks/useAddForceSyncMutation';
import { useDocumentLinkQuery } from '../../hooks/useDocumentLinkManagement';
import useDocumentLinkManagement from './useDocumentLinkManagement';

const DocumentLinkManagementGrid = () => {
    const classes = useStyles();
    const classesGrid = useStylesGrid();

    const queryClient = useQueryClient();

    const { data } = useDocumentLinkQuery();
    const { data: placeholders } = usePlaceholderQuery();
    const { data: attributeTypes } = useAsiteCustomAttributeTypesQuery();

    const {
        idField,
        editField,
        gridState,
        gridResult,
        handleDataStateChange,
        handleItemChange,
        validate,
        inlineEditing: { addNew, add, discard, enterEdit, update, cancel, remove },
        mutations: {
            createDocumentLinkManagementMutation: { isLoading },
        },
    } = useDocumentLinkManagement(data);

    const [dataItem, setDataItem] = useState<any>();
    const [dialogActive, setDialogActive] = useState(false);
    const { showToastSuccess, showToastError } = useToastify();

    const forceSyncMutation = useAddForceSyncMutation();

    const handleOnClickForceSync = () => {
        forceSyncMutation.mutate(dataItem?.id ?? '', {
            onSuccess: (result: any) => {
                if (result.success) {
                    showToastSuccess(`Syncronization for ${dataItem?.sourceDirectory?.fullPath} started`);
                    queryClient.refetchQueries('document-links');
                } else {
                    showToastError(result.errorMessage);
                }
                setDialogActive(!dialogActive);
            },
        });
    };
    const { canManage } = useSecurity();

    return (
        <>
            <ActionConfirmationDialogComponent
                dialogActive={dialogActive}
                setDialogActive={setDialogActive}
                handleOnOk={handleOnClickForceSync}
                text={`Are you sure you want to start force sync for <b>${dataItem?.sourceDirectory?.fullPath}</b> ?`}
            />

            {data ? (
                <>
                    <Grid
                        className={`${classesGrid.grid}`}
                        style={{
                            marginTop: '1%',
                            marginBottom: '2%',
                        }}
                        {...gridState}
                        {...gridResult}
                        sortable={false}
                        filterable={false}
                        onDataStateChange={handleDataStateChange}
                        dataItemKey={idField}
                        editField={editField}
                        onItemChange={handleItemChange}
                    >
                        <GridToolbar>
                            <Box style={{ minHeight: '30px' }} display="flex" justifyContent="space-between">
                                <Typography variant="h6" style={{ color: 'rgba(0, 0, 0, 0.87)' }}>
                                    Document link Management
                                </Typography>
                                {canManage() && (
                                    <Button
                                        onClick={addNew}
                                        variant="contained"
                                        style={{ minWidth: 32, height: 32, padding: 0, borderRadius: '50%' }}
                                    >
                                        <AddIcon style={{ fontSize: 16 }} />
                                    </Button>
                                )}
                            </Box>
                        </GridToolbar>
                        <GridColumn
                            field="isSharedPlaceholder"
                            headerClassName={`${classesGrid.gridColumnHeader}  ${classesGrid.defaultCursor} text-center`}
                            className={`${classesGrid.gridColumn}`}
                            width={'60'}
                            cell={(props: GridCellProps) => <CheckBoxCell {...props} />}
                            title={'S.P.'}
                        />
                        <GridColumn
                            field="sourceDirectory"
                            title={'Source Directory*'}
                            headerClassName={`${classesGrid.gridColumnHeader}  ${classesGrid.defaultCursor}`}
                            className={`${classesGrid.gridColumn}`}
                            cell={(props: GridCellProps) => {
                                return (
                                    <td className={`${classesGrid.gridColumn}  ${classesGrid.wrapText}`}>
                                        <PathFolderModal
                                            {...props}
                                            disabled={props.dataItem.inEdit ? false : true}
                                            pathList={
                                                Boolean(props.dataItem.isSharedPlaceholder) && !_.isEmpty(placeholders)
                                                    ? placeholders
                                                    : []
                                            }
                                        />
                                    </td>
                                );
                            }}
                            width={'auto'}
                        />
                        <GridColumn
                            field="documentType"
                            title={'File Type*'}
                            headerClassName={`${classesGrid.gridColumnHeader}  ${classesGrid.defaultCursor}`}
                            cell={(props: GridCellProps) => {
                                return (
                                    <td className={`${classesGrid.gridColumn}`}>
                                        <AttributeCell
                                            {...props}
                                            disabled={props.dataItem.inEdit ? false : true}
                                            idCustomAttribute={EAtributeTypes.DocumentType}
                                            forDocumentLink={true}
                                        />
                                    </td>
                                );
                            }}
                        />
                        <GridColumn
                            field="status"
                            title={'Status*'}
                            headerClassName={`${classesGrid.gridColumnHeader}  ${classesGrid.defaultCursor}`}
                            cell={(props: GridCellProps) => {
                                return (
                                    <td className={`${classesGrid.gridColumn}`}>
                                        <StatusCell {...props} disabled={props.dataItem.inEdit ? false : true} />
                                    </td>
                                );
                            }}
                        />
                        <GridColumn
                            field="disciplineCode"
                            title={'W-code'}
                            headerClassName={`${classesGrid.gridColumnHeader}  ${classesGrid.defaultCursor}`}
                            cell={(props: GridCellProps) => {
                                return (
                                    <td className={`${classesGrid.gridColumn}`}>
                                        <AttributeCell
                                            {...props}
                                            disabled={props.dataItem.inEdit ? false : true}
                                            idCustomAttribute={EAtributeTypes.DisciplineCode}
                                            forDocumentLink={true}
                                            hasDeleteOption
                                        />
                                    </td>
                                );
                            }}
                        />
                        <GridColumn
                            field="organisation"
                            title={'Organisation*'}
                            headerClassName={`${classesGrid.gridColumnHeader}  ${classesGrid.defaultCursor}`}
                            cell={(props: GridCellProps) => {
                                return (
                                    <td className={`${classesGrid.gridColumn}`}>
                                        <AttributeCell
                                            {...props}
                                            disabled={props.dataItem.inEdit ? false : true}
                                            idCustomAttribute={EAtributeTypes.Organisation}
                                            forDocumentLink={true}
                                        />
                                    </td>
                                );
                            }}
                        />
                        <GridColumn
                            field="purposeOfIssue"
                            title={'POI'}
                            headerClassName={`${classesGrid.gridColumnHeader}  ${classesGrid.defaultCursor}`}
                            cell={(props: GridCellProps) => {
                                return (
                                    <td className={`${classesGrid.gridColumn}`}>
                                        <PoiDropdownCell
                                            {...props}
                                            disabled={props.dataItem.inEdit ? false : true}
                                            hasDeleteOption
                                        />
                                    </td>
                                );
                            }}
                        />
                        <GridColumn
                            field="object"
                            title={'B_Object'}
                            headerClassName={`${classesGrid.gridColumnHeader}  ${classesGrid.defaultCursor}`}
                            cell={(props: GridCellProps) => {
                                return (
                                    <td className={`${classesGrid.gridColumn}`}>
                                        <AttributeCell
                                            {...props}
                                            disabled={props.dataItem.inEdit ? false : true}
                                            idCustomAttribute={EAtributeTypes.Object}
                                            forDocumentLink={true}
                                            hasDeleteOption
                                        />
                                    </td>
                                );
                            }}
                        />

                        <GridColumn
                            field="deelproject"
                            title={'B_Infra'}
                            headerClassName={`${classesGrid.gridColumnHeader}  ${classesGrid.defaultCursor}`}
                            cell={(props: GridCellProps) => {
                                return (
                                    <td className={`${classesGrid.gridColumn} `}>
                                        <AttributeCell
                                            {...props}
                                            disabled={props.dataItem.inEdit ? false : true}
                                            idCustomAttribute={EAtributeTypes.Deelproject}
                                            forDocumentLink={true}
                                            hasDeleteOption
                                        />
                                    </td>
                                );
                            }}
                        />
                        <GridColumn
                            field="linkDirectory"
                            title={'Link Directory*'}
                            headerClassName={`${classesGrid.gridColumnHeader}  ${classesGrid.defaultCursor}`}
                            className={`${classesGrid.gridColumn}`}
                            cell={(props: GridCellProps) => {
                                return (
                                    <td className={`${classesGrid.gridColumn} ${classesGrid.wrapText}`}>
                                        <PathFolderModal
                                            {...props}
                                            disabled={props.dataItem.inEdit ? false : true}
                                            linkDirectory={true}
                                        />
                                    </td>
                                );
                            }}
                            width={'auto'}
                        />
                        <GridColumn
                            field="linkType"
                            title={'Type Link*'}
                            headerClassName={`${classesGrid.gridColumnHeader}  ${classesGrid.defaultCursor}`}
                            cell={(props: GridCellProps) => {
                                return (
                                    <td className={`${classesGrid.gridColumn}`}>
                                        <DropDownCell
                                            {...props}
                                            data={[
                                                { id: 1, name: 'Static' },
                                                { id: 2, name: 'Dynamic' },
                                            ]}
                                            disabled={props.dataItem.inEdit ? false : true}
                                            filterable={false}
                                            textField="name"
                                            dataItemKey="id"
                                        />
                                    </td>
                                );
                            }}
                        />
                        {canManage() && (
                            <GridColumn
                                title=" "
                                width={'120px'}
                                headerClassName={`${classesGrid.gridColumnHeader}  ${classesGrid.defaultCursor}`}
                                className={classesGrid.gridColumn}
                                cell={(props) => (
                                    <InlineEditingActionsCell
                                        {...props}
                                        edit={enterEdit}
                                        remove={remove}
                                        add={add}
                                        discard={discard}
                                        update={update}
                                        cancel={cancel}
                                        idField={idField}
                                        editField={editField}
                                        validate={validate}
                                        isLoading={isLoading}
                                        confirmationMessage={'Are you sure you want to delete this item?'}
                                        withoutHeaderStyle
                                    >
                                        <Button
                                            variant="contained"
                                            size="small"
                                            className={classes.deleteButton}
                                            style={{
                                                backgroundColor: !Boolean(props.dataItem.inEdit) ? '#8dd9c8' : 'grey',
                                                color: 'white',
                                            }}
                                            onClick={() => {
                                                setDataItem(props.dataItem);
                                                setDialogActive(true);
                                            }}
                                            disabled={!Boolean(props.dataItem.inEdit) ? false : true}
                                        >
                                            <Sync className={classes.deleteButtonIcon} />
                                        </Button>
                                    </InlineEditingActionsCell>
                                )}
                            />
                        )}
                    </Grid>
                </>
            ) : (
                <LoadingIndicator />
            )}
        </>
    );
};

export default DocumentLinkManagementGrid;
